import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/auth-context";
import { LanguageProvider } from "@/contexts/language-context";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { MainLayout } from "@/components/layout/main-layout";
import { lazy, Suspense } from "react";
import { LoginPage } from "@/pages/login";
import { RegisterPage } from "@/pages/register";

// Lazy load pages untuk code splitting
const DashboardPage = lazy(() => import("@/pages/dashboard").then(m => ({ default: m.DashboardPage })));
const BudgetPage = lazy(() => import("@/pages/budget").then(m => ({ default: m.BudgetPage })));
const NetWorthPage = lazy(() => import("@/pages/net-worth").then(m => ({ default: m.NetWorthPage })));
const GoalsPage = lazy(() => import("@/pages/goals").then(m => ({ default: m.GoalsPage })));
const ReportsPage = lazy(() => import("@/pages/reports").then(m => ({ default: m.ReportsPage })));
const NotificationsPage = lazy(() => import("@/pages/notifications").then(m => ({ default: m.NotificationsPage })));
const SettingsPage = lazy(() => import("@/pages/settings").then(m => ({ default: m.SettingsPage })));
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/toast";
import { PageSkeleton } from "@/components/shared/page-skeleton";

function AppRoutes() {
  const { user } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
      
      // Listen for notification updates
      const handleNotificationUpdate = () => {
        fetchUnreadNotifications();
      };
      
      window.addEventListener("notification-updated", handleNotificationUpdate);
      
      return () => {
        window.removeEventListener("notification-updated", handleNotificationUpdate);
      };
    }
  }, [user]);

  const fetchUnreadNotifications = async () => {
    try {
      const data = await api.get<{ count: number }>("/notifications/unread");
      setUnreadNotifications(data.count);
    } catch (error) {
      console.error("Failed to fetch unread notifications:", error);
    }
  };

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout unreadNotifications={unreadNotifications} />
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<PageSkeleton />}><DashboardPage /></Suspense>} />
        <Route path="budget" element={<Suspense fallback={<PageSkeleton />}><BudgetPage /></Suspense>} />
        <Route path="net-worth" element={<Suspense fallback={<PageSkeleton />}><NetWorthPage /></Suspense>} />
        <Route path="goals" element={<Suspense fallback={<PageSkeleton />}><GoalsPage /></Suspense>} />
        <Route path="reports" element={<Suspense fallback={<PageSkeleton />}><ReportsPage /></Suspense>} />
        <Route path="notifications" element={<Suspense fallback={<PageSkeleton />}><NotificationsPage /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageSkeleton />}><SettingsPage /></Suspense>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
export { App };
